import { initializeApp, getApps, FirebaseApp } from 'firebase/app';
import {
  getAuth,
  Auth,
  OAuthProvider,
  signInWithPopup,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  User as FirebaseUser,
} from 'firebase/auth';
import {
  getFirestore,
  Firestore,
  doc,
  getDoc,
  setDoc,
  Timestamp,
} from 'firebase/firestore';
import type { UserProfile, UserRole } from '@/types';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  ...(import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
    ? { measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID }
    : {}),
};


// Initialize Firebase
let app: FirebaseApp;
const existingApps = getApps();
if (existingApps.length === 0) {
  app = initializeApp(firebaseConfig);
} else {
  app = existingApps[0]!;
}

export const auth: Auth = getAuth(app);
export const db: Firestore = getFirestore(app);

// Azure AD OIDC Provider
export const azureADProvider = new OAuthProvider('oidc.microsoft');

// Configure Azure AD provider if tenant/client IDs are provided
if (
  import.meta.env.VITE_AZURE_AD_TENANT_ID &&
  import.meta.env.VITE_AZURE_AD_CLIENT_ID
) {
  azureADProvider.setCustomParameters({
    tenant: import.meta.env.VITE_AZURE_AD_TENANT_ID,
  });
}

// Sign in with Microsoft SSO
export const signInWithMicrosoft = async (): Promise<FirebaseUser> => {
  const result = await signInWithPopup(auth, azureADProvider);
  const user = result.user;

  // Set custom claims via Cloud Function (should be called after sign-in)
  // For now, we'll fetch user profile from Firestore
  const userProfile = await getUserProfile(user.uid);
  if (!userProfile) {
    // Create initial profile - role should be set by admin via Cloud Function
    await setDoc(doc(db, 'users', user.uid), {
      uid: user.uid,
      email: user.email || '',
      displayName: user.displayName || '',
      role: 'ANALYST' as UserRole, // Default role, should be updated by admin
      createdAt: Timestamp.now(),
    });
  }

  return user;
};

// Sign out
export const signOut = async (): Promise<void> => {
  await firebaseSignOut(auth);
};

// Get user profile from Firestore
export const getUserProfile = async (
  uid: string
): Promise<UserProfile | null> => {
  const userDoc = await getDoc(doc(db, 'users', uid));
  if (!userDoc.exists()) {
    return null;
  }
  const data = userDoc.data();
  return {
    uid: data.uid,
    email: data.email,
    displayName: data.displayName,
    role: data.role as UserRole,
    createdAt: data.createdAt?.toDate().toISOString() || new Date().toISOString(),
    updatedAt: data.updatedAt?.toDate().toISOString(),
  };
};

// Get current user's role from custom claims or Firestore
export const getCurrentUserRole = async (
  user: FirebaseUser
): Promise<UserRole | null> => {
  // Check custom claims first (set by Cloud Function)
  const token = await user.getIdTokenResult();
  if (token.claims.role) {
    return token.claims.role as UserRole;
  }

  // Fallback to Firestore
  const profile = await getUserProfile(user.uid);
  return profile?.role || null;
};

// Auth state observer helper
export const onAuthStateChange = (
  callback: (user: FirebaseUser | null) => void
) => {
  return onAuthStateChanged(auth, callback);
};

export default app;

